import React, { useEffect, useState } from 'react';
import { listRuns, RunMetadata } from '../api/nucleusApi';
import {
    Table,
    TableRow,
    TableCell,
    Text,
    Link,
    Loader,
    Alert,
    Button,
    Row,
    Column,
    Tooltip,
    SearchField,
} from '@amzn/meridian';

interface RunsListProps {
    onSelectRun: (runId: string) => void;
}

// Column configuration matching MeridianWebReferenceApp pattern
const runsTableColumns = [
    { id: 'runId', label: 'Run ID', width: 200 },
    { id: 'description', label: 'Description', width: 250 },
    { id: 'status', label: 'Status', width: 120 },
    { id: 'submittedBy', label: 'Submitted by', width: 150 },
    { id: 'submittedAt', label: 'Submitted', width: 175, dataAlignment: 'end' as const },
    { id: 'simulationCount', label: 'Simulations', width: 120, dataAlignment: 'end' as const },
];

const RunsList: React.FC<RunsListProps> = ({ onSelectRun }) => {
    const [runs, setRuns] = useState<RunMetadata[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sortColumn, setSortColumn] = useState<string>('');
    const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('ascending');
    const [searchQuery, setSearchQuery] = useState<string>('');

    useEffect(() => {
        loadRuns();
    }, []);

    const loadRuns = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await listRuns();
            setRuns(response.runs);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load runs');
        } finally {
            setLoading(false);
        }
    };

    const formatDateTimeLocal = (dateString?: string): string => {
        if (!dateString) return '–';
        try {
            return new Date(dateString).toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    const formatDateTimeUTC = (dateString?: string): string => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'UTC',
                timeZoneName: 'short',
            });
        } catch {
            return dateString;
        }
    };

    const getStatusColor = (status?: string): 'success' | 'warning' | 'error' | undefined => {
        if (!status) return undefined;
        const lower = status.toLowerCase();
        if (lower === 'completed' || lower === 'success') return 'success';
        if (lower === 'running' || lower === 'in progress') return 'warning';
        if (lower === 'failed' || lower === 'error') return 'error';
        return undefined;
    };

    const capitalizeStatus = (status?: string): string => {
        if (!status) return '';
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    };

    const onSort = ({ sortColumn: col, sortDirection: dir }: { sortColumn: string; sortDirection: 'ascending' | 'descending' }) => {
        setSortColumn(col);
        setSortDirection(dir);
    };

    // Filter runs based on search query
    const filteredRuns = runs.filter((run) => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
            run.runId.toLowerCase().includes(query) ||
            (run.description?.toLowerCase().includes(query) ?? false) ||
            (run.submittedBy?.toLowerCase().includes(query) ?? false) ||
            (run.status?.toLowerCase().includes(query) ?? false)
        );
    });

    // Sort runs if sortColumn is set
    const sortedRuns = [...filteredRuns];
    if (sortColumn) {
        sortedRuns.sort((a, b) => {
            const aVal = a[sortColumn as keyof RunMetadata] ?? '';
            const bVal = b[sortColumn as keyof RunMetadata] ?? '';
            if (aVal < bVal) return sortDirection === 'ascending' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'ascending' ? 1 : -1;
            return 0;
        });
    }

    if (loading) {
        return (
            <Column alignmentHorizontal="center" spacing="400" spacingInset="500">
                <Loader size="large" />
                <Text>Loading simulation runs...</Text>
            </Column>
        );
    }

    if (error) {
        return (
            <Column spacing="400" spacingInset="400">
                <Alert type="error" title="Error loading runs">
                    {error}
                </Alert>
                <Row>
                    <Button onClick={loadRuns}>Retry</Button>
                </Row>
            </Column>
        );
    }

    if (runs.length === 0) {
        return (
            <Column alignmentHorizontal="center" spacing="400" spacingInset="500">
                <Text type="h200">No simulation runs found</Text>
                <Text color="secondary">
                    Runs will appear here once simulation data is available in S3.
                </Text>
            </Column>
        );
    }

    return (
        <Column spacing="400">
            <Row alignmentVertical="center" widths={['fill', 'fit']}>
                <Column>
                    <Text type="h300">Simulation Runs</Text>
                    <Text type="b200" color="secondary">
                        {searchQuery
                            ? `${filteredRuns.length} of ${runs.length} runs`
                            : `${runs.length} run${runs.length !== 1 ? 's' : ''} available`}
                    </Text>
                </Column>
                <SearchField
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onSubmit={() => {}}
                    onClear={() => setSearchQuery('')}
                    placeholder="Search runs..."
                    width={250}
                />
            </Row>

            <Table
                headerRows={1}
                showDividers={true}
                stickyHeaderRow={true}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={onSort}
                spacing="medium"
            >
                <TableRow>
                    {runsTableColumns.map((col) => (
                        <TableCell
                            key={col.id}
                            sortColumn={col.id}
                            width={col.width}
                        >
                            {col.label}
                        </TableCell>
                    ))}
                </TableRow>
                {sortedRuns.map((run) => (
                    <TableRow key={run.runId}>
                        <TableCell header={true}>
                            <Text tag="p">
                                <Link
                                    onClick={() => onSelectRun(run.runId)}
                                    type="secondary"
                                >
                                    {run.runId}
                                </Link>
                            </Text>
                        </TableCell>
                        <TableCell>
                            <Text>{run.description || '–'}</Text>
                        </TableCell>
                        <TableCell>
                            {run.status ? (
                                <Text color={getStatusColor(run.status)}>
                                    {capitalizeStatus(run.status)}
                                </Text>
                            ) : (
                                <Text color="secondary">–</Text>
                            )}
                        </TableCell>
                        <TableCell>
                            <Text>{run.submittedBy || '–'}</Text>
                        </TableCell>
                        <TableCell>
                            {run.submittedAt ? (
                                <Tooltip
                                    position="top"
                                    title={formatDateTimeUTC(run.submittedAt)}
                                >
                                    <Text>{formatDateTimeLocal(run.submittedAt)}</Text>
                                </Tooltip>
                            ) : (
                                <Text color="secondary">–</Text>
                            )}
                        </TableCell>
                        <TableCell>
                            <Text>
                                {run.simulationCount !== undefined ? run.simulationCount : '–'}
                            </Text>
                        </TableCell>
                    </TableRow>
                ))}
            </Table>
        </Column>
    );
};

export default RunsList;
